export type MosaicLayoutKind = "hero" | "half" | "cell";

/** Matches `gap-3` / `minmax(11rem, …)` on the Updates mosaic. */
export const MOSAIC_GAP_PX = 12;
export const MOSAIC_ROW_UNIT_PX = 176; // 11rem at 16px root

export const MOSAIC_SPAN_CELLS: Record<
  MosaicLayoutKind,
  { colSpan: number; rowSpan: number }
> = {
  hero: { colSpan: 4, rowSpan: 2 },
  half: { colSpan: 4, rowSpan: 1 },
  cell: { colSpan: 2, rowSpan: 1 },
};

export interface MosaicPlacement {
  index: number;
  col: number;
  row: number;
  colSpan: number;
  rowSpan: number;
}

/**
 * CSS Grid auto-flow:row placement for the Updates mosaic spans.
 * Preserves input order; does not reorder by size.
 */
export function placeMosaicLayouts(
  layouts: MosaicLayoutKind[],
  columns: number,
): { placements: MosaicPlacement[]; rowCount: number } {
  const occupied = new Set<string>();
  const placements: MosaicPlacement[] = [];
  let rowCount = 0;

  const key = (row: number, col: number) => `${row},${col}`;

  const fits = (row: number, col: number, colSpan: number, rowSpan: number) => {
    if (col + colSpan > columns) return false;
    for (let r = row; r < row + rowSpan; r++) {
      for (let c = col; c < col + colSpan; c++) {
        if (occupied.has(key(r, c))) return false;
      }
    }
    return true;
  };

  const mark = (row: number, col: number, colSpan: number, rowSpan: number) => {
    for (let r = row; r < row + rowSpan; r++) {
      for (let c = col; c < col + colSpan; c++) {
        occupied.add(key(r, c));
      }
    }
  };

  for (let index = 0; index < layouts.length; index++) {
    const layout = layouts[index] ?? "cell";
    const span = MOSAIC_SPAN_CELLS[layout];
    const colSpan = Math.min(span.colSpan, columns);
    const { rowSpan } = span;

    let placed = false;
    for (let row = 0; !placed && row < layouts.length * 4 + 4; row++) {
      for (let col = 0; col <= columns - colSpan; col++) {
        if (!fits(row, col, colSpan, rowSpan)) continue;
        mark(row, col, colSpan, rowSpan);
        placements.push({ index, col, row, colSpan, rowSpan });
        rowCount = Math.max(rowCount, row + rowSpan);
        placed = true;
        break;
      }
    }
  }

  return { placements, rowCount };
}

export function mosaicPlacementStyle(
  placement: MosaicPlacement,
  columns: number,
  gridWidth: number,
): { top: number; left: number; width: number; height: number } {
  const colPitch = (gridWidth - MOSAIC_GAP_PX * (columns - 1)) / columns;
  const rowPitch = MOSAIC_ROW_UNIT_PX + MOSAIC_GAP_PX;
  return {
    top: placement.row * rowPitch,
    left: placement.col * (colPitch + MOSAIC_GAP_PX),
    width: placement.colSpan * colPitch + (placement.colSpan - 1) * MOSAIC_GAP_PX,
    height:
      placement.rowSpan * MOSAIC_ROW_UNIT_PX +
      (placement.rowSpan - 1) * MOSAIC_GAP_PX,
  };
}

export function mosaicGridHeight(rowCount: number): number {
  if (rowCount <= 0) return 0;
  return rowCount * MOSAIC_ROW_UNIT_PX + (rowCount - 1) * MOSAIC_GAP_PX;
}
